import { useState, useEffect, useContext } from "react";
import { Row, Col, Tabs, Dropdown, Menu } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  CheckOutlined,
  SelectOutlined,
  FileTextOutlined,
  DollarOutlined,
  MoreOutlined,
} from "@ant-design/icons";

import { TourContext } from "../../context/TourContext";

import DataTable from "../DataTable";
import CopyableCell from "../CopyableCell";
import {
  clampPage,
  getStoredActiveTab,
  getStoredTabPage,
  resolveActiveTab,
  resolveTabPage,
  setStoredActiveTab,
  setStoredTabPage,
  visibleStatusTabs,
} from "../../utils/tablePaginationStorage";

const { TabPane } = Tabs;

interface IGroupProps {
  groups: any;
  showEditModal: Function;
  handleManage?: Function;
  handleDelete: Function;
  filtered: any;
  showStatementModal?: Function;
  showBulkPaymentModal?: Function;
  storagePrefix?: string;
}

const GroupTable: React.FC<IGroupProps> = ({
  groups,
  showEditModal,
  handleManage,
  handleDelete,
  filtered,
  showStatementModal,
  showBulkPaymentModal,
  storagePrefix = "groups",
}) => {
  const {
    refManageGroup,
    refEditGroup,
    refRemoveGroup,
    refDownloadStatement,
    refBulkPayment,
    markPageAsVisited,
    pages,
  } = useContext(TourContext);

  const initialTab = getStoredActiveTab(storagePrefix);

  const [active, setActive] = useState([]);
  const [inactive, setInactive] = useState([]);
  const [filteredActive, setFilteredActive] = useState([]);
  const [filteredInActive, setFilteredInActive] = useState([]);

  const [state, setState] = useState({
    loading: true,
    activeKey: initialTab,
    pagination: {
      pageSize: 15,
      position: ["bottomRight"],
      total: 0,
      current: getStoredTabPage(storagePrefix, initialTab),
      hideOnSinglePage: true,
    },
    searchItem: "",
  });

  const applyTabTotal = (tabKey: string, total: number) => {
    setState((prev) => {
      if (prev.activeKey !== tabKey) {
        return { ...prev, loading: false };
      }
      const pageSize = prev.pagination.pageSize || 15;
      const current = resolveTabPage(
        storagePrefix,
        tabKey,
        prev.pagination.current || 1,
        total,
        pageSize
      );
      return {
        ...prev,
        loading: false,
        pagination: {
          ...prev.pagination,
          total,
          current,
        },
      };
    });
  };

  const fetchActive = async () => {
    try {
      const activeGroups = groups.filter(
        (item: any) => item.Status.Status === "Active"
      );

      let activeFiltered: any[] = [];
      if (filtered.length > 0) {
        activeFiltered = filtered.filter(
          (item: any) => item.Status.Status === "Active"
        );
        setFilteredActive(activeFiltered);
      } else {
        setFilteredActive([]);
      }

      const total =
        filtered.length > 0 ? activeFiltered.length : activeGroups.length;

      applyTabTotal("1", total);
      setActive(activeGroups);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchInActive = async () => {
    try {
      const inactiveGroups = groups.filter(
        (item: any) => item.Status.Status === "Inactive"
      );

      let inActiveFiltered: any[] = [];
      if (filtered.length > 0) {
        inActiveFiltered = filtered.filter(
          (item: any) => item.Status.Status === "Inactive"
        );
        setFilteredInActive(inActiveFiltered);
      } else {
        setFilteredInActive([]);
      }

      const total =
        filtered.length > 0 ? inActiveFiltered.length : inactiveGroups.length;

      applyTabTotal("2", total);
      setInactive(inactiveGroups);
    } catch (err) {
      console.log(err);
    }
  };

  const handleTableChange = (pagination: any) => {
    setStoredTabPage(storagePrefix, state.activeKey, pagination.current);
    setState((prev) => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        current: pagination.current,
        pageSize: pagination.pageSize ?? prev.pagination.pageSize,
      },
    }));
  };

  useEffect(() => {
    if (filtered.length === 0) {
      setFilteredActive([]);
      setFilteredInActive([]);
    }
    fetchActive();
    fetchInActive();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, filtered]);

  useEffect(() => {
    if (pages.ManageMember) {
      markPageAsVisited("GroupTable");
    }
  }, []);

  const columns = [
    {
      title: "Group Name",
      dataIndex: "GroupName",
      sorter: (a: any, b: any) => a.GroupName.localeCompare(b.GroupName),
    },
    {
      title: "Group Type",
      dataIndex: "IsExternal",
      render: (v: any) => (v ? "External" : "Internal"),
    },
    {
      title: "Tel Number",
      dataIndex: "TelNumber",
    },
    {
      title: "Email",
      dataIndex: "Email",
      render: (v: any) => <CopyableCell value={v} />,
    },
    {
      title: "FSP Number",
      dataIndex: "FSPNumber",
    },
    {
      title: "Group Level",
      dataIndex: "GroupLevel",
      sorter: (a: any, b: any) => a.GroupLevel - b.GroupLevel,
    },
    {
      dataIndex: "Action",
      render: (_: any, record: any) => (
        <Dropdown
          overlay={
            <Menu>
              {handleManage && (
                <Menu.Item
                  key="manage"
                  icon={<SelectOutlined ref={refManageGroup} style={{ color: "green" }} />}
                  onClick={() => handleManage(record.id)}
                >
                  Manage Group
                </Menu.Item>
              )}
              {showStatementModal && (
                <Menu.Item
                  key="statement"
                  icon={<FileTextOutlined ref={refDownloadStatement} style={{ color: "purple" }} />}
                  onClick={() => showStatementModal(record)}
                >
                  Download Statement
                </Menu.Item>
              )}
              {showBulkPaymentModal && (
                <Menu.Item
                  key="bulk"
                  icon={<DollarOutlined ref={refBulkPayment} style={{ color: "#52c41a" }} />}
                  onClick={() => showBulkPaymentModal(record)}
                >
                  Bulk Payment Allocation
                </Menu.Item>
              )}
              <Menu.Item
                key="edit"
                icon={<EditOutlined ref={refEditGroup} style={{ color: "blue" }} />}
                onClick={() => showEditModal(record)}
              >
                Edit Group
              </Menu.Item>
              {state.activeKey === "2" ? (
                <Menu.Item
                  key="activate"
                  icon={<CheckOutlined style={{ color: "orange" }} />}
                  onClick={() => handleDelete(record.id, state.activeKey)}
                >
                  Activate Group
                </Menu.Item>
              ) : (
                <Menu.Item
                  key="remove"
                  icon={<DeleteOutlined ref={refRemoveGroup} style={{ color: "red" }} />}
                  onClick={() => handleDelete(record.id, state.activeKey)}
                >
                  Remove Group
                </Menu.Item>
              )}
            </Menu>
          }
          trigger={["click"]}
        >
          <MoreOutlined
            rotate={90}
            style={{ cursor: "pointer", fontSize: "16px", color: "#000" }}
          />
        </Dropdown>
      ),
    },
  ];

  const tabPanes = [
    {
      key: "1",
      title: "Active",
      content: (
        <DataTable
          persistKey="groups"
          dataSource={filteredActive.length > 0 ? filteredActive : active}
          columns={columns}
          loading={state.loading}
          pagination={state.pagination}
          onChange={handleTableChange}
        />
      ),
      count: filteredActive.length > 0 ? filteredActive.length : active.length,
    },
    {
      key: "2",
      title: "Inactive",
      content: (
        <DataTable
          persistKey="groups"
          dataSource={filteredInActive.length > 0 ? filteredInActive : inactive}
          columns={columns}
          loading={state.loading}
          pagination={state.pagination}
          onChange={handleTableChange}
        />
      ),
      count:
        filteredInActive.length > 0 ? filteredInActive.length : inactive.length,
    },
  ];

  const visiblePanes = visibleStatusTabs(tabPanes);
  const activeKey = resolveActiveTab(state.activeKey, visiblePanes);

  const changeTab = (key: string) => {
    setStoredActiveTab(storagePrefix, key);

    let total = 0;
    if (key === "1") {
      total = filtered.length > 0 ? filteredActive.length : active.length;
    } else if (key === "2") {
      total = filtered.length > 0 ? filteredInActive.length : inactive.length;
    }

    const pageSize = state.pagination.pageSize || 15;
    const storedPage = getStoredTabPage(storagePrefix, key);
    const current = clampPage(storedPage, total, pageSize);
    setStoredTabPage(storagePrefix, key, current);

    setState((prev) => ({
      ...prev,
      activeKey: key,
      loading: false,
      pagination: {
        ...prev.pagination,
        current,
        total,
      },
    }));
  };

  return (
    <>
      <Row>
        <Col span={24}>
          <Tabs
            type="card"
            activeKey={activeKey}
            onChange={changeTab}
          >
            {visiblePanes.map((pane) => (
              <TabPane
                key={pane.key}
                tab={
                  <>
                    <span>{pane.title}</span>
                    <span
                      style={{
                        color: "#B0B0B0",
                        fontWeight: "300",
                        marginLeft: "3px",
                        fontSize: "12px",
                      }}
                    >
                      ({pane.count})
                    </span>
                  </>
                }
              >
                {pane.content}
              </TabPane>
            ))}
          </Tabs>
        </Col>
      </Row>
    </>
  );
};

export default GroupTable;
